import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import OtpInput from 'react-otp-input';
import { getEnvConfig } from '@configs/env';
import { ProviderIcon } from '@configs/providerIcons';
import styled from '@emotion/styled';
import { Button } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';

interface MfaSetupProps {
  /** QR 코드용 otpauth URL */
  qrCodeUrl: string;
  /** 수동 입력용 시크릿 키 */
  secret: string;
  /** OTP 검증 및 설정 완료 핸들러 */
  onVerify: (otpCode: string) => void;
  /** 로딩 상태 */
  isLoading?: boolean;
  /** 에러 메시지 */
  error?: string | null;
}

export const MfaSetup = ({ qrCodeUrl, secret, onVerify, isLoading = false, error }: MfaSetupProps) => {
  const { t } = useTranslation();
  const {
    uiStore: { toastStore },
  } = useStore();
  const { COMPANY_ID } = getEnvConfig();
  const [otp, setOtp] = useState('');
  const [copied, setCopied] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length === 6) {
      onVerify(otp);
    }
  };

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toastStore.open({ message: t('Mfa.setup.copyError') });
      console.error('Failed to copy secret:', err);
    }
  };

  const handleChangeOtp = (otp: string) => {
    setOtp(otp);
  };

  const loginIconWidth = COMPANY_ID === 'ZENITH' ? 181 : 141;

  return (
    <Container>
      <Content>
        <Header>
          <Logo>{ProviderIcon({ providerName: COMPANY_ID, variant: 'login', width: loginIconWidth, height: 25 })}</Logo>
          <Title>{t('Mfa.setup.title')}</Title>
          <Description>{t('Mfa.setup.description')}</Description>
        </Header>

        <QRSection>
          <QRCodeWrapper>
            <QRCodeSVG value={qrCodeUrl} size={160} level="M" />
          </QRCodeWrapper>
          <QRDescription>{t('Mfa.setup.qrDescription')}</QRDescription>
        </QRSection>

        <SecretSection>
          <SecretLabel>{t('Mfa.setup.manualEntry')}</SecretLabel>
          <SecretWrapper>
            <SecretCode>{secret}</SecretCode>
            <CopyButton type="button" onClick={handleCopySecret}>
              {copied ? t('Mfa.setup.copied') : t('Mfa.setup.copy')}
            </CopyButton>
          </SecretWrapper>
        </SecretSection>

        <Form onSubmit={handleSubmit}>
          <OtpSection>
            <OtpLabel>{t('Mfa.setup.otpLabel')}</OtpLabel>
            <OtpInputWrapper>
              <OtpInput
                value={otp}
                onChange={handleChangeOtp}
                numInputs={6}
                shouldAutoFocus
                inputType="number"
                renderInput={props => <OtpInputField {...props} />}
              />
            </OtpInputWrapper>
            {error && <ErrorText>{error}</ErrorText>}
          </OtpSection>

          <SubmitButton
            type="submit"
            size="large"
            variant="contained"
            color="primary"
            fullWidth
            disabled={otp.length !== 6 || isLoading}
          >
            {isLoading ? t('Common.loading') : t('Mfa.setup.complete')}
          </SubmitButton>
        </Form>

        <InfoSection>
          <InfoTitle>{t('Mfa.setup.supportedApps')}</InfoTitle>
          <InfoList>
            <InfoItem>Google Authenticator</InfoItem>
            <InfoItem>Microsoft Authenticator</InfoItem>
          </InfoList>
        </InfoSection>
      </Content>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: calc(100vh - var(--employee-gnb-height));
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors['bg-gray-light']};
  padding: 0px 20px;
  box-sizing: border-box;
`;

const Content = styled.div`
  width: 488px;
  height: 840px;
  max-width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 35px 44px 40px;
  box-sizing: border-box;
  border-radius: 8px;
  box-shadow: 0px 1px 6px 0px rgba(0, 0, 0, 0.06);
  background-color: ${({ theme }) => theme.colors['bg-white']};
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 12px;
`;

const Logo = styled.div`
  width: 100%;
  display: flex;
  align-self: flex-start;
  margin-bottom: 25px;
`;

const Title = styled.h1`
  ${fonts.Headline3}
  margin: 0 0 8px 0;
  text-align: center;
`;

const Description = styled.p`
  ${fonts.Body2}
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
  margin: 0;
  text-align: center;
`;

const QRSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
`;

const QRCodeWrapper = styled.div`
  padding: 16px;
  background-color: ${({ theme }) => theme.colors['bg-white']};
  border: 1px solid ${({ theme }) => theme.colors['border-gray-light']};
  border-radius: 8px;
`;

const QRDescription = styled.p`
  ${fonts.Body4}
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
  margin: 12px 0 0 0;
  text-align: center;
`;

const SecretSection = styled.div`
  width: 100%;
  margin-bottom: 24px;
`;

const SecretLabel = styled.label`
  ${fonts.Body3}
  color: ${({ theme }) => theme.colors['text-gray-main']};
  display: block;
  margin-bottom: 8px;
`;

const SecretWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background-color: ${({ theme }) => theme.colors['bg-gray-light']};
  border-radius: 4px;
`;

const SecretCode = styled.code`
  ${fonts.Body6}
  flex: 1;
  font-family: 'Consolas', 'Monaco', monospace;
  letter-spacing: 2px;
  word-break: break-all;
`;

const CopyButton = styled.button`
  ${fonts.Body4}
  padding: 6px 12px;
  background-color: ${({ theme }) => theme.colors['bg-white']};
  border: 1px solid ${({ theme }) => theme.colors['border-gray-light']};
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: ${({ theme }) => theme.colors['bg-gray-lighter']};
  }
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const OtpSection = styled.div`
  width: 100%;
`;

const OtpLabel = styled.label`
  ${fonts.Body3}
  color: ${({ theme }) => theme.colors['text-gray-main']};
  display: block;
  margin-bottom: 12px;
`;

const OtpInputWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;

  & > div {
    gap: 8px;
  }
`;

const OtpInputField = styled.input`
  width: 48px !important;
  height: 56px;
  ${fonts.Headline3}
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors['border-gray-light']};
  border-radius: 4px;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors['border-primary']};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors['bg-primary-light']};
  }

  /* 숫자 입력 화살표 제거 */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
  appearance: textfield;
`;

const ErrorText = styled.span`
  display: block;
  margin-top: 8px;
  color: ${({ theme }) => theme.colors['text-red-light']};
  ${fonts.Body4};
  text-align: center;
`;

const SubmitButton = styled(Button)`
  box-shadow: none;
  background-color: ${({ theme }) => theme.colors['bg-primary']};
  &:hover {
    box-shadow: none;
  }
  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors['bg-primary-dark']};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const InfoSection = styled.div`
  width: 100%;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid ${({ theme }) => theme.colors['border-gray-lighter']};
`;

const InfoTitle = styled.p`
  ${fonts.Body4}
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
  margin: 0 0 8px 0;
`;

const InfoList = styled.ul`
  margin: 0;
  padding: 0 0 0 16px;
`;

const InfoItem = styled.li`
  ${fonts.Body4}
  color: ${({ theme }) => theme.colors['text-gray-main']};
  margin-bottom: 4px;
`;
